import { Controller } from "@hotwired/stimulus"

import { getConsumer } from "@shared/lib/action-cable/consumer"

/**
 * MCP Debug Controller — Stimulus controller for the admin MCP debug view.
 *
 * Two data sources:
 *   1. XHR fetch from /admin/ai/mcp_debug/logs — used on initial load,
 *      filter changes (tool name + errors-only), and the Refresh button.
 *      Returns the rendered _request_log partial which we drop into the
 *      logList target's innerHTML.
 *   2. ActionCable subscription on McpDebugChannel — used for live new-row
 *      streaming. The model's after_create_commit broadcasts pre-rendered
 *      HTML which we PREPEND to logList so newest sits on top, then auto-
 *      scroll to it.
 *
 * Status pill: streamStatus target shows connecting/connected/disconnected
 * so admins can tell when the live feed is wedged (e.g. cable down) vs
 * just quiet.
 */
export default class extends Controller {
  static targets = [
    "logList",
    "filterTool",
    "filterStatus",
    "refreshBtn",
    "emptyState",
    "filterNotice",
    "streamStatus",
  ]

  static values = {
    logsUrl: String,
    // Set when the page was opened from a Sessions-tab row. Kept as its own
    // value rather than baked into logsUrl, because loadLogs() appends its
    // query string with a literal "?" and would produce a malformed URL if
    // logsUrl already carried one.
    sessionId: String,
    spaceGroupId: String,
    // CONEK-6933: the pill's copy, supplied by the view so no i18n key lives in
    // JS. The English literals below stay as fallbacks, so a missing value can
    // never blank the label out.
    liveLabel: String,
    disconnectedLabel: String,
    connectingLabel: String,
  }

  connect() {
    this.cable = getConsumer(this.spaceGroupIdValue || "default")
    this.subscription = null
    this.subscribe()
    // Initial load — server-rendered HTML is already in the DOM, but we
    // still kick off a fetch so the filter inputs (which start blank)
    // are guaranteed to match the displayed list.
    this.loadLogs()
  }

  disconnect() {
    // Unsubscribe only — this.cable is the page's shared consumer; tearing it
    // down would cut the socket for every other controller using it.
    this.subscription?.unsubscribe()
  }

  subscribe() {
    this.subscription = this.cable.subscriptions.create("McpDebugChannel", {
      connected: () => this.setStatus("connected"),
      disconnected: () => this.setStatus("disconnected"),
      received: (data) => this.handleBroadcast(data),
    })
  }

  setStatus(status) {
    if (!this.hasStreamStatusTarget) return
    const target = this.streamStatusTarget
    target.dataset.status = status
    const dot = target.querySelector("span:first-child")
    const label = target.querySelector("span:last-child")
    if (status === "connected") {
      dot?.classList.remove("bg-amber-500", "bg-red-500")
      dot?.classList.add("bg-green-500")
      if (label) label.textContent = this.liveLabelValue || "Live"
    } else if (status === "disconnected") {
      dot?.classList.remove("bg-amber-500", "bg-green-500")
      dot?.classList.add("bg-red-500")
      if (label)
        label.textContent = this.disconnectedLabelValue || "Disconnected"
    } else {
      dot?.classList.remove("bg-green-500", "bg-red-500")
      dot?.classList.add("bg-amber-500")
      if (label) label.textContent = this.connectingLabelValue || "Connecting…"
    }
  }

  /**
   * Whether a broadcast row belongs in the list currently on screen.
   *
   * The cable stream is per space group, so every row the tenant produces
   * reaches every viewer — including admins who narrowed the list to one
   * session, one tool, or errors only. This mirrors McpDebugController#logs so
   * the live path and the XHR path agree on what the list contains: without it
   * a foreign session's requests were appended to a session-filtered list and
   * the entry count stopped matching the Sessions tab (CONEK-7186).
   *
   * Match semantics follow the server scopes exactly — `for_session` and
   * `for_tool` are equality, `errors_only` is `status_code >= 400`. Anything
   * looser here would show rows a Refresh then removes.
   */
  matchesActiveFilters(data) {
    if (
      this.hasSessionIdValue &&
      this.sessionIdValue &&
      data.session_id !== this.sessionIdValue
    ) {
      return false
    }

    // `params[:tool_name].present?` treats a whitespace-only value as no
    // filter at all, so a stray space must not blank the live feed while the
    // XHR list beside it stays full.
    const toolFilter = this.hasFilterToolTarget
      ? this.filterToolTarget.value
      : ""
    if (toolFilter.trim() !== "" && data.tool_name !== toolFilter) {
      return false
    }

    // Written as `!(… >= 400)` rather than `< 400` so a missing or
    // unparseable status is dropped from an errors-only list instead of
    // sneaking in through NaN.
    if (
      this.hasFilterStatusTarget &&
      this.filterStatusTarget.checked &&
      !(Number(data.status_code) >= 400)
    ) {
      return false
    }

    return true
  }

  handleBroadcast(data) {
    if (data.type !== "log_entry" || !data.html) return
    if (!this.hasLogListTarget) return
    if (!this.matchesActiveFilters(data)) return

    const tmp = document.createElement("div")
    tmp.innerHTML = data.html
    const newEntry = tmp.firstElementChild
    if (!newEntry) return

    // Dedupe: if the same log_id is already rendered (e.g. from a recent
    // XHR refresh that overlapped the broadcast), skip — never show twice.
    const existing = this.logListTarget.querySelector(
      `[data-log-id="${newEntry.dataset.logId}"]`,
    )
    if (existing) return

    // Hide the empty-state placeholder once we have any row.
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    // The "no requests match" notice is written by loadLogs when the filter
    // came back empty. A row that passed the same filter contradicts it, so
    // drop it rather than leaving the notice sitting under a visible row.
    if (this.hasFilterNoticeTarget) {
      this.filterNoticeTarget.remove()
    }

    this.logListTarget.prepend(newEntry)

    // Auto-scroll the page so the admin sees the new row land. Smooth
    // scroll to top of logList rather than the row itself — feels less
    // jumpy when many rows arrive in quick succession.
    this.logListTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async loadLogs() {
    if (!this.hasLogsUrlValue || !this.hasLogListTarget) return

    const params = new URLSearchParams()
    if (this.hasFilterToolTarget && this.filterToolTarget.value) {
      params.set("tool_name", this.filterToolTarget.value)
    }
    if (this.hasFilterStatusTarget && this.filterStatusTarget.checked) {
      params.set("errors_only", "true")
    }
    if (this.hasSessionIdValue && this.sessionIdValue) {
      params.set("session_id", this.sessionIdValue)
    }
    const qs = params.toString()
    const url = qs ? `${this.logsUrlValue}?${qs}` : this.logsUrlValue

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/html",
          "X-Requested-With": "XMLHttpRequest",
        },
      })
      if (!response.ok) {
        return
      }
      const html = await response.text()
      // Empty body = no rows match the current filter. Show a brief
      // notice rather than blanking the panel entirely.
      if (html.trim() === "") {
        this.logListTarget.innerHTML = `
          <div class="p-8 text-center text-sm text-foreground-muted"
               data-mcp--debug-target="filterNotice">
            No requests match the current filter.
          </div>
        `
        return
      }
      this.logListTarget.innerHTML = html
    } catch {
      // Network blip — the live cable feed will refill the panel as new
      // requests arrive, and the next manual Refresh will retry.
    }
  }

  toggleEntry(event) {
    const entry = event.currentTarget.nextElementSibling
    if (entry) {
      entry.classList.toggle("hidden")
    }
  }

  filterLogs() {
    this.loadLogs()
  }

  refresh() {
    this.loadLogs()
  }
}
